import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { GameType, ServerPlayer, ServerSummary } from '@ottd/api';

import { ConnectDialog } from '@/components/prototype/connect-dialog';
import { LiveBadge } from '@/components/prototype/live-badge';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { companyColorHex } from '@/components/prototype/president-face';
import { RelativeTime } from '@/components/prototype/relative-time';
import { TypeTile } from '@/components/prototype/type-tile';
import { Button } from '@/components/ui/button';
import { formatGoalFigure } from '@/lib/format';
import { tr } from '@/lib/locale';
import { serverTypeTheme } from '@/lib/server-type';
import { GoalProgressMeter } from '@/pages/servers/goal-progress-meter';
import { bannerStyle, PlayerLink } from '@/pages/shared';

/** Avatars shown before the row collapses into a "+N" tail. */
const MAX_PLAYER_AVATARS = 5;

/** "Tester placed #1 with 398 pop"; just the name for someone with no company. */
function avatarHint(player: ServerPlayer, type: GameType | null): string {
  if (player.place === null || player.figure === null) return player.name;

  return tr('home:serverCard.placed', {
    name: player.name,
    place: player.place,
    figure: formatGoalFigure(player.figure, type),
  });
}

/**
 * Compact "Live Now" server card (Homepage §1.2). The title and the View
 * button both open the running game; with no game they fall back to the
 * server page. The best time opens the server's time ranking. The header's
 * right column carries the game's two clocks.
 */
export function ServerLiveCard({ server }: { server: ServerSummary }) {
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');
  const theme = serverTypeTheme(server.type);
  const target = server.currentGameId ? `/game/${server.currentGameId}` : `/server/${server.id}`;
  // First place only leads once somebody has made progress - before that
  // there is no goal bar and nobody to frame.
  const hasLeader = server.goalProgress !== null;

  return (
    <article className="bevel bg-panel shadow-hard-sm flex flex-col">
      <div className="flex items-center gap-2.5 p-3" style={bannerStyle(server.type)}>
        <TypeTile type={server.type} size={44} climate={server.climate} />
        <div className="min-w-0">
          <p
            className="font-display truncate text-sm text-white"
            style={{ textShadow: '2px 2px 0 #000' }}
          >
            <Link to={target} className="text-white hover:text-gold">
              {server.name}
            </Link>
          </p>
          <p className="font-mono text-[11px]" style={{ color: theme.subColor }}>
            {server.mapSize ?? '-'}
            {server.currentGameId ? ` · #${server.currentGameId}` : ''}
          </p>
        </div>
        <div className="ml-auto flex shrink-0 flex-col items-end gap-0.5 text-right">
          {server.isLive ? <LiveBadge className="mb-0.5" /> : null}
          {server.inGameDate ? (
            <span className="font-mono text-[11px] text-white">
              {t('serverCard.inGame', { date: server.inGameDate })}
            </span>
          ) : null}
          {server.gameStartedAt ? (
            <span className="font-mono text-[10px]" style={{ color: theme.subColor }}>
              {t('serverCard.started')} <RelativeTime iso={server.gameStartedAt} />
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3.5">
        {/* The goal section takes the card's slack, so the players row and the
            buttons stay on the bottom edge whether or not there is a bar. */}
        <div className="flex flex-1 flex-col gap-2">
          {/* Goal left, the record to beat right; on a narrow card the record
              wraps under and stays right-aligned. */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <p className="text-dim text-[13px]">
              {t('serverCard.goal')} <span className="text-green">{server.goal ?? '-'}</span>
            </p>
            {server.bestTime ? (
              <p className="ml-auto text-right text-[13px]">
                {t('serverCard.bestTime')}{' '}
                {/* Opens the server's top 100 by time, the record at its head. */}
                <Link
                  to={`/server/${server.id}?sort=time`}
                  className="text-gold hover:brightness-118"
                >
                  {server.bestTime}
                </Link>
                {server.recordHolder ? (
                  <>
                    {' '}
                    {t('serverCard.bestTimeBy')} <PlayerLink name={server.recordHolder} />
                  </>
                ) : null}
              </p>
            ) : (
              <p className="text-faint ml-auto text-right text-[13px]">
                {t('serverCard.noRecord')}
              </p>
            )}
          </div>
          {/* Right under the goal it measures; absent until a company has made
              progress. The per-company breakdown lives on the game screen. */}
          {server.goalProgress ? (
            <GoalProgressMeter progress={server.goalProgress} type={server.type} />
          ) : null}
        </div>
        <div className="flex items-end gap-2 pt-1">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-faint text-[13px]">
              {server.playersNow > 0
                ? t('serverCard.playing', { count: server.playersNow })
                : t('serverCard.nobodyPlaying')}
            </span>
            {server.players.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                {server.players.slice(0, MAX_PLAYER_AVATARS).map((player) => (
                  <PlayerLink key={player.name} name={player.name} className="inline-flex flex-col">
                    {/* Gold frame: the one leading the race to the goal. */}
                    <PlayerAvatar
                      name={player.name}
                      src={player.avatar}
                      size={24}
                      hint={avatarHint(player, server.type)}
                      className={hasLeader && player.place === 1 ? 'border-gold' : undefined}
                    />
                    {/* Company colour strip; bg-black/10 is the "no company
                        yet" state, overridden inline once they have one. */}
                    <span
                      className="block h-1 border border-t-0 border-black bg-black/10"
                      style={
                        player.color !== null
                          ? { background: companyColorHex(player.color) }
                          : undefined
                      }
                      data-testid="player-company-strip"
                      aria-hidden
                    />
                  </PlayerLink>
                ))}
                {server.players.length > MAX_PLAYER_AVATARS ? (
                  <span className="text-faint font-mono text-[11px]">
                    +{server.players.length - MAX_PLAYER_AVATARS}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="ml-auto flex shrink-0 gap-2">
            {server.currentGameId ? (
              <Button variant="default" size="sm" asChild>
                <Link to={target}>{tc('actions.view')}</Link>
              </Button>
            ) : null}
            <ConnectDialog
              address={server.address}
              inviteCode={server.inviteCode}
              serverName={server.name}
            />
          </div>
        </div>
      </div>
    </article>
  );
}
